<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { menuItems } from "@/constants/menu";
import { ref, defineEmits, watch } from "vue";
import { MenuItem } from "@/types/menuItem";
import { useRoute, useRouter } from "vue-router";
import { ROUTE_NAMES } from "@/constants/routeNames";
import lockedIcon from "@/assets/icons/locked.svg";
import { useUserStore } from "@/stores/userStore";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const selectedCalculator = ref<MenuItem | null>(null);

const emits = defineEmits(["closeMenu"]);

watch(
  () => route.name as (typeof ROUTE_NAMES)[keyof typeof ROUTE_NAMES],
  (newRouteName) => {
    if (!newRouteName) return;

    const current = menuItems.find(
      (item) =>
        item.menuName === newRouteName ||
        item.matchNames?.includes(newRouteName)
    );
    selectedCalculator.value = current ?? null;
  },
  { immediate: true }
);

function isMenuLocked(menuItem: MenuItem) {
  return (
    menuItem.premiumType !== undefined &&
    !userStore.userHasSubscription(menuItem.premiumType)
  );
}

function openCalculator(menuItem: MenuItem) {
  if (
    menuItem.premiumType !== undefined &&
    !userStore.userHasSubscription(menuItem.premiumType)
  ) {
    let query = {};
    if (userStore.user !== null) {
      query = { openCalculator: menuItem.premiumType };
    }
    router.push({ name: ROUTE_NAMES.USER, query: query });
    return;
  }
  selectedCalculator.value = menuItem;
  if (menuItem.menuName !== null) {
    router.push({ name: menuItem.menuName });
    emits("closeMenu");
  }
}
</script>

<template>
  <div class="flex-1 overflow-y-auto">
    <nav class="flex flex-col w-full p-0 divide-y divide-divider">
      <a
        v-for="item in menuItems"
        :key="item.key"
        class="group flex gap-x-4 items-center justify-between px-4 py-[12px] h-[60px] transition-colors"
        :class="
          selectedCalculator?.key === item.key
            ? 'bg-brown text-white'
            : 'hover:bg-hover text-brown cursor-pointer'
        "
        @click="
          selectedCalculator?.key === item.key ? null : openCalculator(item)
        "
      >
        <!-- Иконка + текст -->
        <div class="flex items-center gap-3">
          <div
            class="min-h-11 min-w-11 bg-white rounded-full flex items-center justify-center"
          >
            <img :src="item.icon" alt="" class="size-8" />
          </div>
          <span
            class="sf-pro-sm text-left transition-colors whitespace-pre-line"
            :class="
              selectedCalculator?.key !== item.key
                ? 'group-hover:text-active'
                : ''
            "
          >
            {{ t(item.key) }}
          </span>
        </div>

        <img
          :src="lockedIcon"
          alt="locked"
          class="size-5"
          v-if="isMenuLocked(item)"
        />
        <span
          v-else
          class="transform transition-transform duration-300 ease-in-out"
          :class="
            selectedCalculator?.key !== item.key
              ? 'text-active  group-hover:rotate-45'
              : 'text-white opacity-35 rotate-45'
          "
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="min-w-[10px] min-h-[10px] max-w-[10px] max-h-[10px]"
          >
            <path
              d="M1 11L11 1M11 1H1M11 1V11"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </a>
    </nav>
  </div>
</template>
